import { portfolioDetailKey, portfolioListKey } from "@/api/hooks/portfolio";
import { PortfolioAssetSelect } from "@/overlay/components/LayerStackWindow/PortfolioEnergySettings/PortfolioAssetSelect";
import { PortfolioManagerButton } from "@/overlay/components/PortfolioManagerWindow/PortfolioManagerButton";
import { PortfolioSelector } from "@/overlay/components/PortfolioManagerWindow/PortfolioSelector";
import { usePortfolioSelection } from "@/overlay/components/PortfolioManagerWindow/usePortfolioSelection";
import { FormField } from "@/overlay/components/form-field";
import { getPortfolioEnergySupport } from "@/portfolio/energyRequests";
import { resolvePortfolioPlotAssets, selectEligiblePortfolioAssetIds } from "@/portfolio/plotAdapter";
import type { Portfolio, PortfolioSummary } from "@/portfolio/types";
import { t } from "@lingui/core/macro";
import { type QueryClient, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

function supportedPortfolio(
  portfolioId: string,
  summaries: PortfolioSummary[] | null | undefined,
  portfolio: Portfolio | null | undefined,
) {
  const summary = summaries?.find((item) => item.id === portfolioId);
  return summary &&
    getPortfolioEnergySupport(summary) === "supported" &&
    portfolio?.id === portfolioId &&
    getPortfolioEnergySupport(portfolio) === "supported"
    ? portfolio
    : undefined;
}

function getApplicablePortfolio(client: QueryClient, portfolioId: string) {
  const listKey = portfolioListKey();
  const detailKey = portfolioDetailKey(portfolioId);
  const listState = client.getQueryState(listKey);
  const detailState = client.getQueryState(detailKey);
  return listState?.status === "success" &&
    listState.fetchStatus !== "fetching" &&
    detailState?.status === "success" &&
    detailState.fetchStatus !== "fetching"
    ? supportedPortfolio(
        portfolioId,
        client.getQueryData<PortfolioSummary[] | null>(listKey),
        client.getQueryData<Portfolio | null>(detailKey),
      )
    : undefined;
}

export function EnergyPortfolioSelector(props: {
  currentPortfolioId?: string;
  includedAssetIds?: string[];
  onApply: (portfolio: Portfolio, includedAssetIds: string[]) => void;
  onIncludedAssetIdsChange?: (includedAssetIds: string[]) => void;
}) {
  const client = useQueryClient();
  const [selectedId, setSelectedId] = useState(props.currentPortfolioId ?? "");
  const [includedAssetIds, setIncludedAssetIds] = useState<string[] | undefined>(props.includedAssetIds);
  const { library, detail, managerVisible } = usePortfolioSelection(selectedId);
  const requestedSelection = useRef<string>();
  const canApply =
    selectedId !== props.currentPortfolioId &&
    !managerVisible &&
    !library.isError &&
    !library.isFetching &&
    !detail.isError &&
    !detail.isFetching &&
    !!supportedPortfolio(selectedId, library.data, detail.data);
  const eligibility = resolvePortfolioPlotAssets(
    detail.data,
    detail.data?.assets.flatMap((asset) => (asset.id ? [asset.id] : [])) ?? [],
  );

  useEffect(
    () => () => {
      requestedSelection.current = undefined;
    },
    [],
  );

  useEffect(() => {
    setIncludedAssetIds(props.includedAssetIds);
  }, [props.includedAssetIds]);

  useEffect(() => {
    if (!canApply || requestedSelection.current === selectedId) {
      return;
    }
    const portfolio = getApplicablePortfolio(client, selectedId);
    if (portfolio) {
      requestedSelection.current = selectedId;
      const assetIds = selectEligiblePortfolioAssetIds(portfolio);
      setIncludedAssetIds(assetIds);
      props.onApply(portfolio, assetIds);
    }
  }, [canApply, client, props.onApply, selectedId]);

  const placeholder = library.isLoading
    ? t`Loading portfolios…`
    : library.isError
      ? t`Portfolios could not be loaded.`
      : library.data?.length === 0
        ? t`No saved portfolios. Add a portfolio in Portfolio Manager, then select it here.`
        : t`Select a saved portfolio`;

  return (
    <>
      <FormField label={t`Portfolio`}>
        <PortfolioSelector
          label={null}
          portfolios={library.data}
          disabled={library.isLoading || library.isError || library.data?.length === 0}
          value={selectedId}
          emptyLabel={placeholder}
          selectedLabel={detail.data?.name}
          onChange={(portfolioId) => {
            requestedSelection.current = undefined;
            setSelectedId(portfolioId);
            setIncludedAssetIds([]);
          }}
          isUnavailable={(portfolio) => getPortfolioEnergySupport(portfolio) !== "supported"}
          optionLabel={(portfolio) =>
            getPortfolioEnergySupport(portfolio) === "unsupported"
              ? t`${portfolio.name} — Unsupported portfolio source`
              : portfolio.name
          }
        />
      </FormField>
      <FormField label={t`Portfolios`}>
        <PortfolioManagerButton label={t`Portfolio Manager`} />
      </FormField>
      {detail.data && eligibility.status === "resolved" && (
        <FormField label={t`Assets`} noSemanticLinking>
          <PortfolioAssetSelect
            assets={detail.data.assets}
            includedAssetIds={includedAssetIds ?? detail.data.assets.flatMap((asset) => (asset.id ? [asset.id] : []))}
            eligibleAssetIds={eligibility.assets.map((asset) => asset.assetId)}
            onChange={(assetIds) => {
              requestedSelection.current = undefined;
              setIncludedAssetIds(assetIds);
              props.onIncludedAssetIdsChange?.(assetIds);
            }}
            showAggregate={false}
          />
        </FormField>
      )}
    </>
  );
}
